import { authClient } from './lib/authClient';
import { trpc } from './lib/trpc';
import {
  Alert,
  Anchor,
  Box,
  Button,
  Checkbox,
  Divider,
  Paper,
  PasswordInput,
  Space,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { BadgeCheck, BadgeX } from 'lucide-react';
import { use } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'wouter';

// !TODO: prevent throwaway email addresses:
// https://github.com/disposable-email-domains/disposable-email-domains?tab=readme-ov-file

export const Signup = () => {
  const [, navigate] = useLocation();
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setError,
  } = useForm<{
    base: string;
    email: string;
    name: string;
    options: {
      data: {
        agreeToEmails: boolean;
        agreeToTos: boolean;
      };
    };
    password: string;
    passwordConfirmation: string;
  }>();

  const settings = use(
    trpc.settings.list.useQuery(undefined, {
      experimental_prefetchInRender: true,
    }).promise,
  );

  const newSignUpsEnabled =
    settings
      ?.find((record) => record.id === 'feature_new_sign_ups')
      ?.value?.toLowerCase() === 'true';

  if (newSignUpsEnabled) {
    const submitHandler = handleSubmit(async (formData) => {
      if (formData.password !== formData.passwordConfirmation) {
        setError('passwordConfirmation', {
          message: 'Passwords do not match',
        });
        return;
      }

      const { error } = await authClient.signUp.email({
        email: formData.email,
        name: formData.name,
        password: formData.password,
      });

      if (error) {
        setError('base', {
          message: error.message,
        });
      } else {
        notifications.show({
          message: (
            <>
              <BadgeCheck stroke="var(--color-lime-400)" /> Sign up successful!
            </>
          ),
        });
        navigate('/');
      }
    });

    return (
      <Paper
        className="w-1/2 mx-auto mt-20"
        p="xl"
        radius="md"
        shadow="md"
        withBorder
      >
        <Title order={3}>Sign up</Title>
        <Space h="sm" />
        <Divider />
        <Space h="sm" />
        {errors.base && (
          <>
            <Alert
              color="red"
              icon={<BadgeX />}
              title=""
              variant="filled"
            >
              {errors?.base?.message?.toString()}
            </Alert>
            <Space h="sm" />
          </>
        )}
        <form
          noValidate
          onSubmit={submitHandler}
        >
          <TextInput
            error={errors?.email?.message?.toString()}
            label="Email address"
            required
            type="email"
            {...register('email', {
              required: 'Please provide an email address',
            })}
          />
          <Space h="md" />
          <TextInput
            error={errors?.name?.message?.toString()}
            label="Name"
            required
            type="text"
            {...register('name', {
              required: 'Please provide your name',
            })}
          />
          <Space h="md" />
          <PasswordInput
            error={errors?.password?.message?.toString()}
            label="Password"
            required
            {...register('password', {
              required: 'Please provide a password',
            })}
          />
          <Space h="md" />
          <PasswordInput
            error={errors?.passwordConfirmation?.message?.toString()}
            label="Password Confirmation"
            required
            {...register('passwordConfirmation', {
              required: 'Please provide a password confirmation',
            })}
          />
          <Space h="xl" />
          <Checkbox
            error={errors?.options?.data?.agreeToTos?.message?.toString()}
            required
            {...register('options.data.agreeToTos', {
              required: 'You must agree to the terms of service',
            })}
            label={
              <Box>
                I agree to the{' '}
                <a
                  href="https://clew.app/tos/"
                  rel="noreferrer"
                  target="_blank"
                >
                  Terms of Service
                </a>{' '}
                <Box
                  display="inline"
                  style={{ color: 'var(--mantine-color-red-6)' }}
                >
                  *
                </Box>
              </Box>
            }
          />
          <Space h="xl" />
          <Checkbox
            label="I agree to receive occasional emails from Suspense"
            {...register('options.data.agreeToEmails')}
          />
          <Space h="xl" />
          <Button
            disabled={isSubmitting}
            loading={isSubmitting}
            type="submit"
          >
            Sign up
          </Button>
        </form>
        <Space h="xl" />
        <Anchor
          component={Link}
          to="/signin"
        >
          Sign in
        </Anchor>
      </Paper>
    );
  }

  return (
    <div className="inset-0 absolute flex flex-col justify-center text-center">
      Sign ups are currently not available. Please try again later 🙏
    </div>
  );
};
